import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled
} from '@mui/material';
import {
  DeliveryDining as CourierIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  Assignment as OrderIcon,
  CheckCircle as CompletedIcon,
  CheckCircle,
  Schedule as PendingIcon,
  TrendingUp as StatsIcon,
  Person as PersonIcon,
  MoreVert as MoreVertIcon,
  Settings as SettingsIcon,
  Timeline as TrackingIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import GuideButton from '../common/guide/GuideButton';
import { Helmet } from 'react-helmet-async';
import ownAuthSession from '../../services/auth/ownAuthSession';
import { useAuth } from '../../contexts/AuthContext';
import AddCourierModal from './AddCourierModal';
import CourierOrderSettingsModal from './CourierOrderSettingsModal';
import CourierTracking from './CourierTracking';
import axios from 'axios';
import { mainApi } from '../../config/apiClient';
import { peekPendingCourierTracking, COURIER_TRACKING_EVENT } from '../aichat/agent/uiBridge';
import { updatePersonnel } from '../../services/migration/personnelDataService';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Leaflet default icon fix
// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
});

// Kurye veri tipi - SQL API response'a uygun
interface Courier {
  id: number;
  firebaseUid: string;
  name: string;
  email?: string;
  phone?: string;
  status: 'active' | 'busy' | 'offline';
  totalDeliveries: number;
  completedToday: number;
  rating: number;
  currentLocation?: string;
  lastLatitude?: number;
  lastLongitude?: number;
  lastLocationUpdate?: string;
  branchIds?: string;
  branchNames?: string;
  restaurantOrders: boolean;
  ecommerceOrders: boolean;
  isActive: boolean;
  assignedAt?: string;
}

// Harita modal için styled component
const MapModalContainer = styled(Box)(({ theme }) => ({
  height: 400,
  width: '100%',
  '& .leaflet-container': {
    height: '100%',
    width: '100%',
    borderRadius: theme.shape.borderRadius,
  }
}));

const CourierManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width: 1499px)');
  const { t } = useTranslation('salesSystem');
  const { userDetails, branches } = useAuth();
  const companyId = userDetails?.companyId;
  const [loading, setLoading] = useState(true);
  const [couriers, setCouriers] = useState<Courier[]>([]);
  const [addCourierModalOpen, setAddCourierModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Kurye takip ekranı için state
  const [showTrackingScreen, setShowTrackingScreen] = useState(false);

  // AI ajan köprüsü (openCourierTracking): istek varsa takip ekranına geç;
  // filtreler CourierTracking mount olunca istekten TÜKETİLİR (peek temizlemez).
  useEffect(() => {
    if (peekPendingCourierTracking()) setShowTrackingScreen(true);
    const onAgentEvent = () => setShowTrackingScreen(true);
    window.addEventListener(COURIER_TRACKING_EVENT, onAgentEvent);
    return () => window.removeEventListener(COURIER_TRACKING_EVENT, onAgentEvent);
  }, []);
  
  // Sayfalama için state'ler
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 10 : 25);

  // Silme modalı için state'ler
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [courierToDelete, setCourierToDelete] = useState<Courier | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Snackbar için state'ler
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  // Menü için state'ler
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCourier, setSelectedCourier] = useState<Courier | null>(null);

  // Sipariş ayarları modalı için state'ler
  const [orderSettingsModalOpen, setOrderSettingsModalOpen] = useState(false);
  const [courierForSettings, setCourierForSettings] = useState<Courier | null>(null);
  
  // Konum harita modalı için state'ler
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [courierForLocation, setCourierForLocation] = useState<Courier | null>(null);
  
  // Liste yenileme trigger'ı
  const [refreshKey, setRefreshKey] = useState(0);

  // Kuryeleri SQL API'den yükle
  useEffect(() => {
    if (!companyId) return;

    const loadCouriers = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🔍 Kuryeler SQL API\'den yükleniyor...', { companyId });
        
        const response = await mainApi.get(`/api/Couriers`);
        
        if (response.data.success) {
          const apiCouriers = response.data.data || [];
          
          console.log('📦 API Response:', apiCouriers);
          console.log('🏢 AuthContext Branches:', branches);
          console.log('🏢 Branch IDs:', branches.map(b => ({ id: b.id, name: b.name })));
          
          // API response'u frontend Courier tipine dönüştür
          const courierList: Courier[] = apiCouriers.map((c: any) => {
            // Şube ID'lerini şube isimlerine çevir
            let branchNames = t('courierManagement.list.noBranchAssigned');
            let branchIdsStr = '';
            
            // branchIds string veya array olabilir, kontrol et
            console.log(`🔍 Kurye ${c.name} için branchIds:`, c.branchIds, 'Type:', typeof c.branchIds, 'IsArray:', Array.isArray(c.branchIds));
            
            if (c.branchIds && typeof c.branchIds === 'string') {
              branchIdsStr = c.branchIds;
              const branchIdArray = c.branchIds.split(',').filter((id: string) => id.trim());
              const branchNameArray = branchIdArray.map((branchId: string) => {
                const branch = branches.find(b => b.id === branchId.trim());
                console.log(`  📍 Şube arama: "${branchId.trim()}" -> Bulunan:`, branch);
                return branch?.name || t('courierManagement.list.unknownBranch');
              });
              branchNames = branchNameArray.join(', ') || t('courierManagement.list.noBranchAssigned');
            } else if (Array.isArray(c.branchIds)) {
              branchIdsStr = c.branchIds.join(',');
              const branchNameArray = c.branchIds.map((branchId: string) => {
                const trimmedId = branchId?.trim?.() || branchId;
                const branch = branches.find(b => b.id === trimmedId);
                console.log(`  📍 Şube arama (array): "${trimmedId}" -> Bulunan:`, branch);
                return branch?.name || t('courierManagement.list.unknownBranch');
              });
              branchNames = branchNameArray.join(', ') || t('courierManagement.list.noBranchAssigned');
            }
            
            return {
              id: c.id,
              firebaseUid: c.firebaseUid,
              name: c.name || t('courierManagement.list.unnamedCourier'),
              email: c.email || '',
              phone: c.phoneNumber || '',
              status: c.status === 'active' ? 'active' : 
                      c.status === 'busy' ? 'busy' : 'offline',
              totalDeliveries: c.totalDeliveries || 0,
              completedToday: c.completedToday || 0,
              rating: c.rating || 5.0,
              currentLocation: c.currentLocationText || t('courierManagement.list.noLocationInformation'),
              lastLatitude: c.lastLatitude,
              lastLongitude: c.lastLongitude,
              lastLocationUpdate: c.lastLocationUpdate,
              branchIds: branchIdsStr,
              branchNames: branchNames,
              restaurantOrders: c.restaurantOrders ?? true,
              ecommerceOrders: c.ecommerceOrders ?? true,
              isActive: c.isActive ?? true,
              assignedAt: c.assignedAt
            };
          });

          courierList.sort((a, b) => a.name.localeCompare(b.name));
          
          console.log('✅ Kuryeler SQL\'den yüklendi', { 
            totalCouriers: courierList.length,
            couriers: courierList.map(c => ({ id: c.id, name: c.name, status: c.status }))
          });
          
          setCouriers(courierList);
        } else {
          throw new Error(response.data.message || t('courierManagement.errors.failedToLoadCouriers'));
        }
      } catch (error) {
        console.error('❌ Kurye yükleme hatası:', error);
        setError(t('courierManagement.errors.loadCourierData'));
      } finally {
        setLoading(false);
      }
    };

    loadCouriers();
  }, [companyId, branches, refreshKey]);

  const handleAddCourier = () => {
    setAddCourierModalOpen(true);
  };

  const handleCourierTracking = () => {
    setShowTrackingScreen(true);
  };

  const handleBackFromTracking = () => {
    setShowTrackingScreen(false);
  };

  const handleCourierAdded = () => {
    console.log('🔄 Kurye eklendi, liste otomatik olarak güncellenecek');
    // Firebase listener sayesinde liste otomatik olarak güncellenecek
    // Ek bir işlem yapmaya gerek yok
  };

  // Kurye silme modalını aç
  const handleDeleteCourier = (courier: Courier) => {
    setCourierToDelete(courier);
    setDeleteModalOpen(true);
  };

  // Silme modalını kapat
  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setCourierToDelete(null);
  };

  // Kurye silme işlemi
  const handleConfirmDelete = async () => {
    if (!courierToDelete || !companyId) return;

    setDeleting(true);
    
    try {
      console.log('🗑️ Kurye siliniyor...', { 
        courierId: courierToDelete.id,
        firebaseUid: courierToDelete.firebaseUid,
        courierName: courierToDelete.name 
      });

      // own-auth oturum kontrolü
      const uid = ownAuthSession.getCurrentUid();
      if (!uid) {
        throw new Error('No authenticated user found');
      }

      // 1. Kurye rolünü kaldır — .NET HR API (SystemUsers.Role = 'employee'). Node/Firebase yok.
      await updatePersonnel(courierToDelete.firebaseUid, { role: 'employee' });
      console.log('✅ Kurye rolü SQL\'de employee olarak güncellendi');

      // 2. SQL'den kurye kaydını sil (soft delete)
      console.log('📤 SQL delete isteği gönderiliyor...', {
        firebaseUid: courierToDelete.firebaseUid
      });

      const sqlDeleteResponse = await mainApi.post(
        `/api/couriers/delete`,
        {
          firebaseUid: courierToDelete.firebaseUid,
          companyId: companyId
        }
      );

      console.log('📥 SQL delete yanıtı:', {
        status: sqlDeleteResponse.status,
        data: sqlDeleteResponse.data
      });

      if (!sqlDeleteResponse.data.success) {
        console.warn('⚠️ SQL kurye silme başarısız:', sqlDeleteResponse.data.message);
      } else {
        console.log('✅ Kurye SQL\'den de başarıyla silindi');
      }

      console.log('✅ Kurye başarıyla silindi', { 
        courierId: courierToDelete.id,
        firebaseUid: courierToDelete.firebaseUid,
        courierName: courierToDelete.name 
      });

      // Başarı mesajı göster
      setSnackbarMessage(t('courierManagement.snackbar.permissionsRemoved', { name: courierToDelete.name }));
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Modal'ı kapat
      handleCloseDeleteModal();

    } catch (error) {
      console.error('❌ Kurye silme hatası:', error);
      
      let errorMessage = t('courierManagement.errors.removeCourier');
      if (axios.isAxiosError(error)) {
        errorMessage = error.response?.data?.message || error.message;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }

      setSnackbarMessage(errorMessage);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  // Snackbar kapatma
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // Menü açma
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, courier: Courier) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedCourier(courier);
  };

  // Menü kapatma
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCourier(null);
  };

  // Sipariş ayarları
  const handleOrderSettings = () => {
    if (selectedCourier) {
      console.log('🔧 Kurye sipariş ayarları açılıyor:', selectedCourier);
      setCourierForSettings(selectedCourier); // Kurye bilgisini ayrı state'e kaydet
      setOrderSettingsModalOpen(true);
    }
    handleMenuClose();
  };

  // Silme işlemi (mevcut fonksiyonu kullan)
  const handleDeleteFromMenu = () => {
    if (selectedCourier) {
      handleDeleteCourier(selectedCourier);
    }
    handleMenuClose();
  };

  // Konum haritasını aç
  const handleOpenLocationModal = (courier: Courier, event: React.MouseEvent) => {
    event.stopPropagation();
    if (courier.lastLatitude && courier.lastLongitude) {
      console.log('📍 Kurye konum modalı açılıyor:', courier.name, courier.lastLatitude, courier.lastLongitude);
      setCourierForLocation(courier);
      setLocationModalOpen(true);
    }
  };

  // Konum modalını kapat
  const handleCloseLocationModal = () => {
    setLocationModalOpen(false);
    setCourierForLocation(null);
  };

  // Sayfalandırılmış kurye verileri
  const paginatedCouriers = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return couriers.slice(startIndex, startIndex + rowsPerPage);
  }, [couriers, page, rowsPerPage]);

  // Sayfa değişimi
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
  };

  // Sayfa başına satır sayısı değişimi
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Kurye takip ekranı gösteriliyorsa onu render et
  if (showTrackingScreen) {
    return <CourierTracking onBack={handleBackFromTracking} />;
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'busy':
        return 'warning';
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return t('courierManagement.status.active');
      case 'busy':
        return t('courierManagement.status.busy');
      case 'offline':
        return t('courierManagement.status.offline');
      default:
        return t('courierManagement.status.unknown');
    }
  };

  if (loading) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Box textAlign="center">
            <CircularProgress size={60} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('courierManagement.loading')}
            </Typography>
          </Box>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          {t('courierManagement.actions.refreshPage')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Başlık */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={4}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={{ xs: 2, sm: 0 }}
      >
        <Box display="flex" alignItems="center">
          <CourierIcon sx={{ fontSize: { xs: 32, sm: 40 }, color: theme.palette.primary.main, mr: 2 }} />
          <Typography 
            variant="h4" 
            component="h1" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}
          >
            {t('courierManagement.title')}
          </Typography>
          <GuideButton guideKey="couriers.management" sx={{ ml: 1 }} />
        </Box>
        <Box display="flex" gap={1} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="large"
            sx={{ 
              borderRadius: 2, 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              padding: { xs: '8px 16px', sm: '10px 22px' }
            }}
            onClick={handleAddCourier}
          >
            {t('courierManagement.actions.addCourier')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<TrackingIcon />}
            size="large"
            sx={{ 
              borderRadius: 2, 
              width: { xs: '100%', sm: 'auto' },
              fontSize: { xs: '0.875rem', sm: '0.9375rem' },
              padding: { xs: '8px 16px', sm: '10px 22px' }
            }}
            onClick={handleCourierTracking}
          >
            {t('courierManagement.actions.courierTracking')}
          </Button>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <Grid container spacing={3} mb={4}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierManagement.stats.totalCouriers')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold">
                    {couriers.length}
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierManagement.stats.activeCouriers')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="success.main">
                    {couriers.filter(c => c.status === 'active').length}
                  </Typography>
                </Box>
                <CheckCircle sx={{ fontSize: 40, color: theme.palette.success.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierManagement.stats.todaysDeliveries')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="info.main">
                    {couriers.reduce((sum, c) => sum + c.completedToday, 0)}
                  </Typography>
                </Box>
                <OrderIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2, boxShadow: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    {t('courierManagement.stats.averageRating')}
                  </Typography>
                  <Typography variant="h4" component="div" fontWeight="bold" color="warning.main">
                    {couriers.length > 0 
                      ? (couriers.reduce((sum, c) => sum + c.rating, 0) / couriers.length).toFixed(1)
                      : '0.0'
                    }
                  </Typography>
                </Box>
                <StatsIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Kurye Listesi */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3 }}>
        <Box p={3}>
          <Typography variant="h6" component="h2" fontWeight="bold" mb={2}>
            {t('courierManagement.list.title', { count: couriers.length })}
          </Typography>
          
          {couriers.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              {t('courierManagement.list.empty')}
            </Alert>
          ) : (
            <>
              {/* 📱 MOBİL: Kart Görünümü */}
              {isMobile ? (
                <Grid container spacing={2}>
                  {paginatedCouriers.map((courier) => (
                    <Grid item xs={12} sm={6} key={courier.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          borderRadius: 2,
                          borderLeft: 4,
                          borderLeftColor: courier.status === 'active' ? 'success.main' : 
                                          courier.status === 'busy' ? 'warning.main' : 'error.main',
                          '&:hover': { boxShadow: 3 }
                        }}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          {/* Üst: Avatar + İsim + Durum + 3 Nokta Menü */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                              <Avatar sx={{ 
                                width: 40, 
                                height: 40, 
                                bgcolor: theme.palette.primary.main,
                                fontSize: 16
                              }}>
                                {courier.name.charAt(0)}
                              </Avatar>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                  {courier.name}
                                </Typography>
                                {courier.phone && (
                                  <Typography variant="caption" color="text.secondary">
                                    📞 {courier.phone}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Chip 
                                label={getStatusText(courier.status)}
                                color={getStatusColor(courier.status) as any}
                                size="small"
                                sx={{ fontSize: '0.7rem', height: 22 }}
                              />
                              <IconButton 
                                size="small" 
                                onClick={(e) => handleMenuOpen(e, courier)}
                              >
                                <MoreVertIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>

                          <Divider sx={{ my: 1 }} />

                          {/* Alt: İstatistikler 2x2 Grid */}
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'primary.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierManagement.list.today')}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary.main">
                                  {courier.completedToday}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierManagement.list.total')}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="info.main">
                                  {courier.totalDeliveries}
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'warning.50', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierManagement.list.rating')}
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="warning.main">
                                  {courier.rating} ⭐
                                </Typography>
                              </Box>
                            </Grid>
                            <Grid item xs={6}>
                              <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  {t('courierManagement.list.branch')}
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" noWrap>
                                  {courier.branchNames || t('courierManagement.list.unassigned')}
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>

                          {/* Konum */}
                          {courier.currentLocation && courier.currentLocation !== t('courierManagement.list.noLocationInformation') && (
                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {courier.currentLocation}
                              </Typography>
                            </Box>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                /* 💻 MASAÜSTÜ: Tablo Görünümü */
                <TableContainer component={Paper} sx={{ 
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                  borderRadius: 2,
                  overflow: 'auto',
                  maxHeight: { xs: '60vh', sm: '70vh' }
                }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: { xs: 120, sm: 150 }
                        }}>{t('courierManagement.table.courier')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', sm: 'table-cell' },
                          minWidth: 120
                        }}>{t('courierManagement.table.contact')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 80
                        }}>{t('courierManagement.table.status')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', md: 'table-cell' },
                          minWidth: 60
                        }}>{t('courierManagement.table.today')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', md: 'table-cell' },
                          minWidth: 60
                        }}>{t('courierManagement.table.total')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', lg: 'table-cell' },
                          minWidth: 60
                        }}>{t('courierManagement.table.rating')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', md: 'table-cell' },
                          minWidth: 120
                        }}>{t('courierManagement.table.branch')}</TableCell>
                        <TableCell sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          display: { xs: 'none', lg: 'table-cell' },
                          minWidth: 120
                        }}>{t('courierManagement.table.location')}</TableCell>
                        <TableCell align="right" sx={{ 
                          backgroundColor: 'primary.main',
                          color: 'white',
                          fontWeight: 'bold',
                          py: 1,
                          minWidth: 80
                        }}>{t('courierManagement.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedCouriers.map((courier) => (
                        <TableRow 
                          key={courier.id} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                            '& .MuiTableCell-root': { 
                              py: 0.5,
                              height: 32,
                              fontSize: '0.775rem'
                            }
                          }}
                        >
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Avatar sx={{ 
                                width: 32, 
                                height: 32, 
                                mr: 1, 
                                bgcolor: theme.palette.primary.main,
                                fontSize: 14
                              }}>
                                {courier.name.charAt(0)}
                              </Avatar>
                              <Typography variant="body2" fontWeight="medium">
                                {courier.name}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Typography variant="body2" sx={{ 
                              maxWidth: '120px', 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              {courier.email || '-'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {courier.phone || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={getStatusText(courier.status)}
                              color={getStatusColor(courier.status) as any}
                              size="small"
                              sx={{ fontSize: '11px', height: 24 }}
                            />
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {courier.completedToday}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2">
                              {courier.totalDeliveries}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" fontWeight="medium">
                                {courier.rating}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" ml={0.5}>
                                ⭐
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                            <Typography variant="body2" sx={{
                              maxWidth: '120px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: courier.branchNames === 'No Branch Assigned' ? 'text.secondary' : 'text.primary'
                            }}>
                              {courier.branchNames || t('courierManagement.list.noBranchAssigned')}
                            </Typography>
                          </TableCell>
                          <TableCell 
                            sx={{ display: { xs: 'none', lg: 'table-cell' } }}
                            onClick={(e) => courier.lastLatitude && courier.lastLongitude ? handleOpenLocationModal(courier, e) : undefined}
                          >
                            <Box 
                              display="flex" 
                              alignItems="center"
                              sx={{
                                cursor: courier.lastLatitude && courier.lastLongitude ? 'pointer' : 'default',
                                '&:hover': courier.lastLatitude && courier.lastLongitude ? {
                                  color: 'primary.main',
                                  '& .MuiSvgIcon-root': { color: 'primary.main' },
                                  '& .MuiTypography-root': { color: 'primary.main' }
                                } : {}
                              }}
                            >
                              <LocationIcon sx={{ fontSize: 14, color: courier.lastLatitude ? 'success.main' : 'text.secondary', mr: 0.5 }} />
                              <Typography variant="caption" color={courier.lastLatitude ? 'text.primary' : 'text.secondary'} sx={{
                                maxWidth: '140px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {courier.currentLocation}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <IconButton 
                              size="small" 
                              onClick={(e) => handleMenuOpen(e, courier)}
                              title={t('courierManagement.table.actions')}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {couriers.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                            <Typography color="text.secondary">
                              {t('courierManagement.list.noCourierFound')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              {/* Sayfalama */}
              {couriers.length > 0 && (
                <TablePagination
                  component="div"
                  count={couriers.length}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={isMobile ? [5, 10, 25] : [10, 25, 50, 100]}
                  labelRowsPerPage={isMobile ? t('courierManagement.pagination.page') : t('courierManagement.pagination.rowsPerPage')}
                  labelDisplayedRows={({ from, to, count }) => 
                    isMobile ? `${from}-${to} / ${count}` : t('courierManagement.pagination.displayedRows', { from, to, count })
                  }
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Add Courier Modal */}
      <AddCourierModal
        open={addCourierModalOpen}
        onClose={() => setAddCourierModalOpen(false)}
        onCourierAdded={handleCourierAdded}
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6">{t('courierManagement.deleteModal.title')}</Typography>
        </DialogTitle>
        
        <DialogContent>
          {courierToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                {t('courierManagement.deleteModal.confirmText', { name: courierToDelete.name })}
              </Typography>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t('courierManagement.deleteModal.afterAction')}</strong>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                  <li>{t('courierManagement.deleteModal.effects.cannotLogin')}</li>
                  <li>{t('courierManagement.deleteModal.effects.permissionsRemoved')}</li>
                  <li>{t('courierManagement.deleteModal.effects.roleReverted')}</li>
                  <li>{t('courierManagement.deleteModal.effects.historyPreserved')}</li>
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={handleCloseDeleteModal}
            disabled={deleting}
            variant="outlined"
          >
            {t('courierManagement.actions.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            disabled={deleting}
            variant="contained"
            color="error"
            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleting ? t('courierManagement.deleteModal.removing') : t('courierManagement.deleteModal.removePermissions')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 180,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleOrderSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="info" />
          </ListItemIcon>
          <ListItemText primary={t('courierManagement.menu.orderSettings')} />
        </MenuItem>
        
        <MenuItem onClick={handleDeleteFromMenu} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText primary={t('courierManagement.menu.removePermissions')} />
        </MenuItem>
      </Menu>

      {/* Courier Order Settings Modal */}
      <CourierOrderSettingsModal
        open={orderSettingsModalOpen}
        onClose={() => {
          setOrderSettingsModalOpen(false);
          setCourierForSettings(null);
          // Listeyi yenile - şube değişikliklerini görmek için
          setRefreshKey(prev => prev + 1);
        }}
        courier={courierForSettings}
      />

      {/* Kurye Konum Harita Modalı */}
      <Dialog
        open={locationModalOpen}
        onClose={handleCloseLocationModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <LocationIcon color="primary" />
          <Box>
            <Typography variant="h6">{t('courierManagement.locationModal.title', { name: courierForLocation?.name || '' })}</Typography>
            {courierForLocation?.lastLocationUpdate && (
              <Typography variant="caption" color="text.secondary">
                {t('courierManagement.locationModal.lastUpdate')}: {new Date(courierForLocation.lastLocationUpdate).toLocaleString('en-US')}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {courierForLocation?.lastLatitude && courierForLocation?.lastLongitude && (
            <MapModalContainer>
              <MapContainer
                center={[courierForLocation.lastLatitude, courierForLocation.lastLongitude]}
                zoom={15}
                scrollWheelZoom={true}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <Marker position={[courierForLocation.lastLatitude, courierForLocation.lastLongitude]}>
                  <Popup>
                    <Box sx={{ minWidth: 150 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {courierForLocation.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {courierForLocation.email}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {courierForLocation.phone}
                      </Typography>
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="caption" display="block">
                          <strong>{t('courierManagement.locationModal.coordinates')}:</strong>
                        </Typography>
                        <Typography variant="caption" display="block">
                          {courierForLocation.lastLatitude?.toFixed(6)}, {courierForLocation.lastLongitude?.toFixed(6)}
                        </Typography>
                      </Box>
                    </Box>
                  </Popup>
                </Marker>
              </MapContainer>
            </MapModalContainer>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => {
              if (courierForLocation?.lastLatitude && courierForLocation?.lastLongitude) {
                window.open(
                  `https://www.google.com/maps?q=${courierForLocation.lastLatitude},${courierForLocation.lastLongitude}`,
                  '_blank'
                );
              }
            }}
            variant="outlined"
            startIcon={<LocationIcon />}
          >
            {t('courierManagement.locationModal.openInGoogleMaps')}
          </Button>
          <Button 
            onClick={handleCloseLocationModal}
            variant="contained"
          >
            {t('courierManagement.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CourierManagement;
